import isEmpty from "lodash/isEmpty";
import { t } from "@/i18n-js/instance";
import type { Action } from "@circle-react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { useFetchPaywallPrice } from "@circle-react/components/SettingsApp/Workflows/hooks/useFetchPaywallPrice";
import { Typography } from "@circle-react-uikit/Typography";
import { Badge } from "../../../Badge";
import { BaseView } from "../../BaseView";

const i18nRoot = "settings.workflows.view";

interface PaywallTrialApiParams {
  paywall_id: string;
  paywall_price_id: string;
  send_notification_subscription_created_by_admin: boolean;
  trial_days: number;
}

interface ViewPaywallTrialProps {
  action?: Action & {
    api_params?: PaywallTrialApiParams;
  };
}

const renderNotiFyOption = (
  send_notification_subscription_created_by_admin: boolean,
): string =>
  send_notification_subscription_created_by_admin ? t("yes") : t("no");

const renderPaywallName = (
  paywallPrice:
    | { paywall_name?: string; paywall_display_name?: string }
    | undefined,
  isLoading: boolean,
) => (
  <div>
    {isLoading ? (
      <Badge isLoading />
    ) : (
      <Typography.LabelSm>
        {!paywallPrice || isEmpty(paywallPrice)
          ? t("settings.workflows.view.deleted_paywall")
          : paywallPrice.paywall_name || paywallPrice.paywall_display_name}
      </Typography.LabelSm>
    )}
  </div>
);

const renderPaywallPriceName = (
  paywallPrice: { display_name?: string } | undefined,
  isLoading: boolean,
) => (
  <div>
    {isLoading ? (
      <Badge isLoading />
    ) : (
      <Typography.LabelSm>
        {!paywallPrice || isEmpty(paywallPrice)
          ? t("settings.workflows.view.deleted_paywall_price")
          : paywallPrice.display_name}
      </Typography.LabelSm>
    )}
  </div>
);

const renderTrialDays = (trialDays: number | undefined, isLoading: boolean) => (
  <div>
    {isLoading ? (
      <Badge isLoading />
    ) : (
      <Typography.LabelSm>{trialDays ?? "-"}</Typography.LabelSm>
    )}
  </div>
);

const renderNotificationOption = (
  shouldSend: boolean | undefined,
  isLoading: boolean,
) => (
  <div>
    {isLoading ? (
      <Badge isLoading />
    ) : (
      <Typography.LabelSm>
        {renderNotiFyOption(shouldSend ?? false)}
      </Typography.LabelSm>
    )}
  </div>
);

export const ViewPaywallTrial = ({ action }: ViewPaywallTrialProps) => {
  const apiParams = action?.api_params;
  const paywallPriceId = apiParams?.paywall_price_id;
  const paywallId = apiParams?.paywall_id;
  const trialDays = apiParams?.trial_days;
  const shouldSendNotificationSubscriptionCreatedByAdmin =
    apiParams?.send_notification_subscription_created_by_admin;
  const { paywallPrice, isLoading } = useFetchPaywallPrice({
    paywallId: paywallId || "",
    priceId: paywallPriceId || "",
  });

  if (!paywallPrice && !isLoading) {
    return null;
  }

  return (
    <BaseView>
      <BaseView.Section>
        <BaseView.Title>{t([i18nRoot, "resources.paywall"])}</BaseView.Title>
        {renderPaywallName(paywallPrice, isLoading)}
      </BaseView.Section>
      <BaseView.Section>
        <BaseView.Title>
          {t([i18nRoot, "resources.paywall_price"])}
        </BaseView.Title>
        {renderPaywallPriceName(paywallPrice, isLoading)}
      </BaseView.Section>
      <BaseView.Section>
        <BaseView.Title>{t([i18nRoot, "trial_days"])}</BaseView.Title>
        {renderTrialDays(trialDays, isLoading)}
      </BaseView.Section>
      <BaseView.Section>
        <BaseView.Title>
          {t([i18nRoot, "send_notification_subscription_created_by_admin"])}
        </BaseView.Title>
        {renderNotificationOption(
          shouldSendNotificationSubscriptionCreatedByAdmin,
          isLoading,
        )}
      </BaseView.Section>
    </BaseView>
  );
};
